import { Modal, ModalBody, ModalContent, ModalHeader } from '@nextui-org/react'
import { VscClose } from '@react-icons/all-files/vsc/VscClose'
import type { ReactNode } from 'react'
import { bridge } from '~/services/bridge'
import { cm } from '~/utils/utils'
import { HeaderContent } from './HeaderContent'

type Size = 'md' | 'sm' | 'lg' | 'xl' | '2xl' | 'xs' | '3xl' | '4xl' | '5xl' | 'full'
type ScrollBehavior = 'normal' | 'inside' | 'outside'
interface BaseModalProps {
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void
  title: string | ReactNode
  isDismissable?: boolean
  size?: Size
  subtitle?: string
  children?: ReactNode
  scrollBehavior?: ScrollBehavior
  className?: string
  backdrop?: 'opaque' | 'transparent' | 'blur'
  parentRef?: React.RefObject<HTMLDivElement | null>
  headerBorder?: boolean
  contentClassName?: string
  bodyClassName?: string
  wrapperClassName?: string
  showCloseButton?: boolean
  centered?: boolean
}

function BaseModal({
  isOpen,
  onOpenChange,
  title,
  isDismissable = true,
  size = 'md',
  subtitle = undefined,
  children = null,
  scrollBehavior = 'normal',
  className = '',
  backdrop = 'opaque',
  parentRef,
  headerBorder = true,
  contentClassName = '',
  bodyClassName = '',
  wrapperClassName = '',
  showCloseButton = true,
  centered = false
}: BaseModalProps) {
  const portalContainer = parentRef?.current || document.body
  const isPlugin = bridge.isInPlugin()
  return (
    <Modal
      portalContainer={portalContainer}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      isDismissable={isDismissable}
      hideCloseButton
      size={size}
      className={cm('bg-white rounded-xl', isPlugin && 'max-w-[calc(100vw-24px)]', className)}
      scrollBehavior={scrollBehavior}
      backdrop={backdrop}
      classNames={{
        wrapper: cm(
          centered
            ? 'fixed inset-0 flex items-center justify-center z-[999999] bg-black/30'
            : 'absolute h-full w-full shadow-heavy z-[999999]',
          wrapperClassName
        ),
        base: centered
          ? cm('w-full z-[999999]', isPlugin && 'max-w-[calc(100vw-24px)]')
          : cm('h-full w-full z-[999999]', isPlugin && 'max-w-[calc(100vw-24px)]'),
        backdrop: cm('absolute h-full w-full z-[999999]', wrapperClassName)
      }}
    >
      <ModalContent
        className={cm(
          'dark:bg-neutral-900 dark:hover:bg-medium7 rounded-xl shadow-2xl ring-1 ring-slate-700/15',
          contentClassName
        )}
      >
        {closeModal => (
          <>
            <ModalHeader
              className={cm(
                'flex items-center justify-between pl-[20px] pr-[5px] dark:bg-medium7 py-0',
                headerBorder ? 'border-b-1 border-[rgba(0,0,0,0.12)]' : ''
              )}
            >
              <HeaderContent title={title} subtitle={subtitle} />
              {showCloseButton && (
                <div className='flex items-center justify-center w-[50px] h-[50px]'>
                  <div
                    className='flex items-center justify-center rounded-xl hover:bg-[#ececec] p-2 cursor-pointer'
                    onClick={() => closeModal()}
                  >
                    <VscClose aria-label='Close' className='hover:bg-light' />
                  </div>
                </div>
              )}
            </ModalHeader>
            <ModalBody
              className={cm(
                'p-[20px] dark:bg-medium7',
                centered ? '' : 'h-[calc(100%-50px)] min-h-[450px]',
                bodyClassName
              )}
            >
              {children}
            </ModalBody>
          </>
        )}
      </ModalContent>
    </Modal>
  )
}

export default BaseModal
